import type { CityConfig } from "../city-template/types";

export const palatineConfig: CityConfig = {
  city: "Palatine",
  state: "Illinois",
  stateAbbr: "IL",
  slug: "palatine",
  route: "/cities/palatine",
  title: "Emergency Passport Palatine IL | Expedited Passport Services | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Palatine, Illinois — as fast as 24 hours. Fast passport renewal and new passport applications for Palatine, Rolling Meadows, Inverness, and all northwest suburban Cook County residents. A+ BBB rated registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Palatine, IL",
  heroSubtitle: "Northwest suburban family trip to Ireland with two weeks notice? Palatine business traveler heading to Frankfurt and your passport is expired? Rolling Meadows corporate traveler needing a rush renewal for Asia? We help Palatine and northwest suburban residents get their passports fast — as fast as 24 hours. A+ BBB rated registered State Dept. courier.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Palatine%20Illinois%20northwest%20Chicago%20suburb%20with%20historic%20downtown%20district%20featuring%20vintage%20brick%20buildings%20along%20Northwest%20Highway%2C%20Palatine%20Metra%20station%2C%20Harper%20College%20modern%20campus%20buildings%2C%20Deer%20Grove%20Forest%20Preserve%20natural%20scenery%20with%20walking%20trails%2C%20tree-lined%20suburban%20residential%20streets%20with%20classic%20homes%2C%20warm%20summer%20afternoon%20golden%20light%2C%20peaceful%20suburban%20community%20photography&width=1800&height=700&seq=palatine-hero-001&orientation=landscape",
  heroImageAlt: "Palatine Illinois northwest suburban downtown district and tree-lined neighborhoods",
  geoPosition: "42.1103;-88.0342",
  geoPlacename: "Palatine, Illinois",
  lastModified: "2026-06-04",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "101 W Congress Pkwy, 4th Floor, Chicago, IL 60605",
    distance: "Approximately 45 minutes southeast of Palatine via I-90 or Metra UP-NW Line",
  },
  nearbyCitiesZips: [
    { name: "Palatine", zips: "60067, 60074, 60055" },
    { name: "Rolling Meadows", zips: "60008" },
    { name: "Inverness", zips: "60067" },
    { name: "Arlington Heights", zips: "60004, 60005" },
    { name: "Buffalo Grove", zips: "60089" },
    { name: "Wheeling", zips: "60090" },
    { name: "Hoffman Estates", zips: "60169, 60192" },
    { name: "Schaumburg", zips: "60173, 60193, 60194" },
    { name: "Barrington", zips: "60010" },
    { name: "Lake Zurich", zips: "60047" },
    { name: "Deer Park", zips: "60010" },
    { name: "Prospect Heights", zips: "60070" },
    { name: "Mount Prospect", zips: "60056" },
    { name: "Des Plaines", zips: "60016, 60018" },
    { name: "Elk Grove Village", zips: "60007" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier to Chicago Passport Agency — 45 minutes away but you skip the trip" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-building-line", title: "Northwest Suburban Hub", desc: "Serving Palatine, Rolling Meadows, and northwest Cook and southern Lake counties" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels — no downtown Chicago trip required" },
  ],
  internalLinks: [
    { name: "Expedited passport in Arlington Heights, IL", route: "/cities/arlington-heights" },
    { name: "Emergency passport help in Schaumburg, IL", route: "/cities/schaumburg" },
    { name: "Urgent passport services in Chicago, IL", route: "/cities/chicago" },
    { name: "Illinois passport services overview", route: "/states/illinois" },
  ],
  fpcOfficeAddress: "101 W Congress Pkwy, 4th Floor, Chicago, IL 60605",
  fpcOfficeNote: "Fast Passport Center serves Palatine, Illinois remotely via secure registered courier. Our registered couriers hand-deliver your documents to the Chicago Passport Agency (45 minutes southeast via I-90 or the Metra UP-NW Line) — without you making the trip. Palatine residents can ship from any UPS or FedEx in northwest Cook County.",
  fpcOfficeDistance: "Serving Palatine, Rolling Meadows, Inverness, Arlington Heights, Buffalo Grove, Wheeling, Hoffman Estates, Schaumburg, Barrington, Lake Zurich, and all of northwest suburban Cook County",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout Palatine and Chicago's northwest suburbs. From the historic downtown Palatine district and Metra station to the Harper College campus, from the corporate offices of Rolling Meadows to the estate communities of Inverness and Barrington, from the forest preserves of Deer Grove to the bustling retail corridors along Dundee Road — we serve all of the northwest suburban corridor.",
  nearbyCitiesIntro: "We serve Palatine and all nearby communities including Rolling Meadows, Inverness, Arlington Heights, Buffalo Grove, Wheeling, Hoffman Estates, Schaumburg, Barrington, Lake Zurich, Deer Park, Prospect Heights, Mount Prospect, Des Plaines, and Elk Grove Village. We also serve all of northwest Cook County and southern Lake County.",
  howItWorksIntro: "Most Palatine travelers complete everything online and ship documents overnight. No need to drive 45 minutes to downtown Chicago — our couriers handle the Chicago Passport Agency trip for you.",
  mistakesIntro: "Palatine is one of the northwest suburbs' largest communities with a thriving corporate corridor, Harper College, and a family-oriented international travel culture. Here are the most common passport mistakes we see from Palatine and northwest suburban travelers.",
  testimonialsIntro: "Palatine and northwest suburban travelers — from Rolling Meadows corporate professionals to Harper College families to Inverness residents exploring the world — trust us when their departure is days away.",
  faqIntro: "Have questions about expedited passports in Palatine, IL? Here are the most common ones we hear from northwest suburban Cook County travelers.",
  statePageRoute: "/states/illinois",
  statePageName: "Illinois",
  dmvWebsite: "https://www.ilsos.gov/facilities/facilitylist.html",
  dmvLabel: "Illinois Secretary of State — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Must drive 45 minutes to downtown Chicago", "Full day commitment — downtown parking and navigation"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Chicago Passport Agency — hand-delivered", "No drive to downtown Chicago required", "Perfect for northwest suburban corporate and family travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Chicago Passport Agency — hand-delivered", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Palatine travelers with immediate departure needs — as fast as 24 hours. Our couriers use the Chicago Passport Agency — 45 minutes away but you never make the trip.",
      bullets: ["24–48 hour turnaround available", "Chicago Passport Agency direct courier", "Northwest suburban family and corporate travel support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Palatine adults with a passport issued within the last 15 years. Skip the post office appointment entirely.",
      bullets: ["No clerk visit required for most renewals", "Mail-in or courier options", "Returned to your Palatine address"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport (First-Time)",
      description: "First-time passport application for Palatine residents — including families, Harper College students, and new citizens.",
      bullets: ["Acceptance facility scheduling", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Emergency passport replacement for Palatine residents who lost their passport before international travel.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement in 24–48 hours"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 — for Palatine and northwest suburban families with kids traveling internationally.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change Passport",
      description: "Passport name correction after marriage, divorce, or legal name changes for Palatine and Cook County residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Palatine IL", description: "Urgent passport processing for Palatine Illinois travelers with immediate departure needs." },
    { name: "Expedited Passport Renewal Palatine IL", description: "Fast passport renewal for Palatine Illinois residents with a previously-issued passport." },
    { name: "First-Time Passport Palatine IL", description: "New passport application services for Palatine Illinois residents applying for their first passport." },
    { name: "Child Passport Palatine IL", description: "Minor passport services for Palatine Illinois families with children under 16." },
  ],
  testimonials: [
    {
      name: "Jennifer M.",
      location: "Palatine, IL",
      rating: 5,
      text: "Family trip to Ireland with 10 days notice and my passport had expired. Fast Passport Center got my renewal in 3 days. Shipped from UPS on Northwest Highway. Didn't miss a day of work and my kids got to see the Cliffs of Moher. Incredible service!",
      service: "Expedited Passport Renewal",
    },
    {
      name: "Tom R.",
      location: "Rolling Meadows, IL",
      rating: 5,
      text: "Corporate trip to Singapore with 6 days notice. Fast Passport Center had my business passport in 2 days. I shipped from FedEx near the corporate campus on Golf Road. The dedicated agent was phenomenal — understood corporate travel urgency perfectly.",
      service: "Emergency Passport",
    },
    {
      name: "Susan K.",
      location: "Inverness, IL",
      rating: 5,
      text: "Two child passports for a family trip to Australia. Fast Passport Center walked us through everything — both parents consent, DS-11 forms, photos. Both passports arrived in 4 days. Amazing for northwest suburban families!",
      service: "Child Passport",
    },
  ],
  facilities: [
    {
      id: "pltn-1",
      name: "Palatine Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "140 E Wood St",
      city: "Palatine",
      state: "IL",
      zip: "60067",
      phone: "(847) 358-2239",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Downtown Palatine, near Metra Station",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary Palatine acceptance facility. Appointments required — book 1–2 weeks in advance. Convenient to downtown Palatine and Metra UP-NW Line.",
      icon: "ri-mail-line",
    },
    {
      id: "pltn-2",
      name: "Arlington Heights Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "909 W Euclid Ave",
      city: "Arlington Heights",
      state: "IL",
      zip: "60005",
      phone: "(847) 259-1340",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Arlington Heights, approximately 5 miles southeast of Palatine",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Arlington Heights and southern Palatine residents. Near Arlington Town Square.",
      icon: "ri-mail-line",
    },
    {
      id: "pltn-3",
      name: "Buffalo Grove Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "255 N Buffalo Grove Rd",
      city: "Buffalo Grove",
      state: "IL",
      zip: "60089",
      phone: "(847) 459-2791",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Buffalo Grove, approximately 5 miles east of Palatine",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Buffalo Grove and eastern Palatine residents. Near Buffalo Grove Town Center.",
      icon: "ri-mail-line",
    },
    {
      id: "pltn-4",
      name: "Hoffman Estates Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "990 W Higgins Rd",
      city: "Hoffman Estates",
      state: "IL",
      zip: "60169",
      phone: "(847) 882-0066",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Hoffman Estates, approximately 6 miles southwest of Palatine",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Hoffman Estates and western Palatine residents. Near I-90 and Prairie Stone area.",
      icon: "ri-mail-line",
    },
    {
      id: "pltn-5",
      name: "Schaumburg Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "450 W Schaumburg Rd",
      city: "Schaumburg",
      state: "IL",
      zip: "60194",
      phone: "(847) 882-7455",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Schaumburg, approximately 7 miles south of Palatine",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Large facility serving Schaumburg and southern Palatine area. Near Woodfield Mall.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, 2–3 week wait", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 45 min drive to downtown Chicago", fpc: "No — ship from Palatine" },
    { feature: "Parking & Navigation", agency: "Downtown Chicago parking costs", fpc: "Ship from your neighborhood" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
  ],
  mistakes: [
    {
      icon: "ri-calendar-close-line",
      title: "Summer Vacation Rush",
      description: "Palatine families planning summer trips to Ireland, Europe, and beyond often discover passport issues in June. Routine processing takes 6–13 weeks — start expedited service at least 6 weeks before your trip to avoid summer peak delays.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Passport Photo",
      description: "Local photo services along Northwest Highway and Dundee Road sometimes produce photos that meet ID standards but fail passport requirements. Photos must be exactly 2x2 inches on a white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Forms",
      description: "Missing signatures or incorrect form types cause rejections at Palatine and Arlington Heights acceptance facilities. Verify whether you need DS-11 (first-time) or DS-82 (renewal) before your appointment.",
    },
    {
      icon: "ri-road-map-line",
      title: "Underestimating the Chicago Trip",
      description: "Palatine is 45 minutes from the Chicago Passport Agency via I-90. While this seems manageable, downtown parking and agency wait times turn it into a full-day ordeal. Our courier handles the entire trip for you.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent for Minors",
      description: "Both parents must appear or submit notarized consent for child passports. Palatine and northwest suburban families often arrive with only one parent at acceptance facilities — leading to wasted appointments.",
    },
    {
      icon: "ri-global-line",
      title: "College Study Abroad Timing",
      description: "Harper College students and Palatine high school graduates heading abroad need a passport valid at least 6 months past their return date. Start the process as soon as you're accepted — don't wait until right before departure.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes — from anywhere in the Palatine area or northwest suburbs." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS or FedEx near Northwest Highway or Dundee Road." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy — experienced with corporate travel, family documentation, and student study abroad needs." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Our registered courier submits to the Chicago Passport Agency — just 45 minutes away but you never make the trip." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Palatine address — trackable every step of the way." },
  ],
  faqs: [
    { q: "How fast can I get an emergency passport in Palatine, IL?", a: "Fast Passport Center offers emergency passport processing in Palatine, Illinois in as little as 24–48 hours. Our registered couriers hand-deliver your documents to the Chicago Passport Agency (45 minutes southeast via I-90) — without you making the trip." },
    { q: "Does Palatine have its own passport agency?", a: "No — Palatine does not have a regional passport agency. The nearest facility is the Chicago Passport Agency (approximately 45 minutes southeast via I-90 or Metra UP-NW Line). Fast Passport Center's courier service means Palatine residents never need to make the trip." },
    { q: "Can I get an expedited passport in Rolling Meadows or Buffalo Grove?", a: "Absolutely. Fast Passport Center serves all northwest suburbs including Rolling Meadows, Inverness, Arlington Heights, Buffalo Grove, Wheeling, Hoffman Estates, Schaumburg, Barrington, and all northwest Cook and southern Lake County communities." },
    { q: "What airport do Palatine travelers use for international flights?", a: "Chicago O'Hare International Airport (ORD) is approximately 25 minutes south of Palatine via I-90 and I-294. O'Hare is one of the world's busiest airports with direct flights worldwide — very convenient for northwest suburban travelers." },
    { q: "Where can I get passport photos in Palatine?", a: "Walgreens and CVS locations along Northwest Highway and Dundee Road offer passport photos. FedEx Office and UPS Store on Rand Road also provide them. Photos must be 2x2 inches on a white background, taken within 6 months." },
    { q: "Can I get a child's passport expedited in Palatine?", a: "Yes. Fast Passport Center provides child passport services for minors under 16 throughout Palatine and the northwest suburbs. Both parents or legal guardians must typically be present at the acceptance facility." },
    { q: "Do you serve Harper College students and faculty?", a: "Yes. Fast Passport Center serves Harper College students, faculty, and staff. We understand study abroad deadlines, academic conference timelines, and student travel documentation requirements." },
    { q: "How much does an expedited passport cost in Palatine?", a: "Government fees are fixed: $130 for a first-time adult passport book, $130 + $60 expedite fee for a renewed adult passport. Fast Passport Center's service fees vary by speed and type. Contact us for a quote based on your timeline." },
  ],
  photoLocations: [
    { name: "Walgreens — Palatine", address: "500 E Northwest Hwy, Palatine, IL 60074", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS — Palatine", address: "235 E Palatine Rd, Palatine, IL 60067", hours: "Mon–Sun: 8AM–10PM" },
    { name: "UPS Store — Palatine", address: "1361 E Dundee Rd, Palatine, IL 60074", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "FedEx Office — Arlington Heights", address: "110 W Rand Rd, Arlington Heights, IL 60004", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
  ],
  dmvLocations: [
    { name: "Illinois SOS — Schaumburg", address: "1275 Ridge Ave, Schaumburg, IL 60193", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Illinois Secretary of State for driver's licenses, REAL ID, and vehicle registration. Closest to Palatine — approximately 7 miles south via Roselle Road. Online reservations strongly recommended.", featured: true },
    { name: "Illinois SOS — Des Plaines", address: "1470 Lee St, Des Plaines, IL 60018", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Serves northwest Cook County. Full REAL ID processing available. Approximately 12 miles southeast of Palatine.", featured: false },
  ],
  airports: [
    {
      code: "ORD",
      name: "Chicago O'Hare International Airport",
      distance: "Approximately 25 minutes south via I-90 and I-294",
      address: "10000 W O'Hare Ave, Chicago, IL 60666",
      note: "Primary international airport for Palatine and the northwest suburbs. United and American Airlines hub with direct flights worldwide. Very convenient — essentially a straight shot down I-90.",
      flightUrl: "https://www.flychicago.com/ohare/",
      flightLabel: "ORD Flight Info",
      featured: true,
    },
    {
      code: "MDW",
      name: "Chicago Midway International Airport",
      distance: "Approximately 50 minutes southeast via I-290 and I-55",
      address: "5700 S Cicero Ave, Chicago, IL 60638",
      note: "Southwest Airlines hub. Further from Palatine but good for Southwest domestic connections and international routes to the Caribbean and Mexico.",
      flightUrl: "https://www.flychicago.com/midway/",
      flightLabel: "MDW Flight Info",
      featured: false,
    },
  ],
};